import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, Control, FieldValues, FieldPath } from "react-hook-form";
import { FaFile, FaHandPointer } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";

interface FileUploadProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  accept?: string;
  multiple?: boolean;
}

const FileUpload = <TFieldValues extends FieldValues>({
  control,
  name,
  accept = "*/*",
  multiple = false,
}: FileUploadProps<TFieldValues>) => {
  const parseAccept = (accept: string) => {
    if (accept === "*/*") {
      return {};
    }

    const mimeTypes = accept
      .split(",")
      .reduce((acc: Record<string, string[]>, type) => {
        acc[type.trim()] = [];
        return acc;
      }, {});

    return mimeTypes;
  };

  const FileUploadContent = ({
    field,
  }: {
    field: {
      onChange: (value: File | File[] | null) => void;
      value: File | File[] | null;
    };
  }) => {
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
          if (multiple) {
            field.onChange(acceptedFiles);
          } else {
            field.onChange(acceptedFiles[0]);
          }
        }
      },
      [field],
    );

    const removeFile = useCallback(
      (index?: number) => {
        if (
          multiple &&
          Array.isArray(field.value) &&
          typeof index === "number"
        ) {
          const newFiles = field.value.filter(
            (_: File, i: number) => i !== index,
          );
          field.onChange(newFiles.length > 0 ? newFiles : null);
        } else {
          field.onChange(null);
        }
      },
      [field],
    );

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
      onDrop,
      accept: parseAccept(accept),
      multiple: multiple,
      noClick: true,
    });

    const hasFile =
      field.value &&
      (Array.isArray(field.value) ? field.value.length > 0 : true);
    const files = Array.isArray(field.value)
      ? field.value
      : field.value
        ? [field.value]
        : [];

    return (
      <div className="w-full">
        <div
          {...getRootProps()}
          className={`select-none cursor-pointer flex flex-col gap-4 mt-2 border-2 rounded-xl items-center px-4 py-8 ${
            hasFile
              ? "border-solid border-primary"
              : "border-dashed border-primary border-opacity-50"
          }`}
          onClick={open}
        >
          <input {...getInputProps()} />
          {hasFile ? (
            <div className="flex flex-col gap-2 items-center w-full">
              {files.map((file: File, index: number) => (
                <div
                  key={index}
                  className="flex items-center gap-2 w-full justify-between p-2 bg-gray-50 rounded"
                >
                  <div className="flex items-center gap-2">
                    <FaFile />
                    <span className="text-sm font-medium">
                      {file.name.length > 20
                        ? file.name.slice(0, 10) + "..." + file.name.slice(-10)
                        : file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-red-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(multiple ? index : undefined);
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4 items-center">
              <FaFile />
              <span className="text-sm">
                {isDragActive
                  ? `Отпустите файл${multiple ? "ы" : ""} здесь...`
                  : `Перетащите файл${multiple ? "ы" : ""}`}
              </span>
              <div className="flex flex-row gap-8 p-2 items-center w-full justify-center">
                <div className="block w-full border-solid border-b-1 border-default max-w-[100px]"></div>
                <span className="text-sm">или</span>
                <div className="block w-full border-solid border-b-1 border-default max-w-[100px]"></div>
              </div>
              <div className="flex flex-col gap-4 items-center">
                <FaHandPointer />
                <span className="text-sm">
                  Нажмите, чтобы выбрать файл{multiple ? "ы" : ""}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => <FileUploadContent field={field} />}
    />
  );
};

export default FileUpload;
